import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Boat, ArrowRight, Calendar, Scales, User, Play } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import StartVesselModal from "./StartVesselModal";

function formatCountdown(msRemaining) {
    if (msRemaining <= 0) return "Arriving";
    const totalMinutes = Math.floor(msRemaining / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function NextVesselCountdown() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [next, setNext] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [startOpen, setStartOpen] = useState(false);

    const canStart = user?.role === "supervisor";

    const load = async () => {
        try {
            const { data } = await api.get("/lineup/next");
            setNext(data && data.id ? data : null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        load();
        const refresh = setInterval(load, 5 * 60 * 1000);
        const tick = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => {
            clearInterval(refresh);
            clearInterval(tick);
        };
    }, []);

    const remaining = useMemo(() => {
        if (!next) return 0;
        return new Date(next.start_datetime).getTime() - now;
    }, [next, now]);

    const onStarted = async () => {
        // Refresh and jump to the new active vessel
        try {
            const { data } = await api.get("/vessels", { params: { status: "active" } });
            const v = (data || [])[0];
            setStartOpen(false);
            setNext(null);
            if (v?.id) nav(`/vessel/${v.id}`);
            else load();
        } catch {
            setStartOpen(false);
            load();
        }
    };

    if (!loaded) return null;

    if (!next) {
        return (
            <div className="g-card p-4 mb-5 flex items-center gap-4" data-testid="next-vessel-empty" style={{ borderLeft: "4px solid var(--grindrod-navy)" }}>
                <Boat size={28} weight="duotone" className="text-slate-400" />
                <div className="flex-1">
                    <div className="uppercase-label text-[10px] text-slate-500">Next Scheduled Vessel</div>
                    <div className="text-sm text-slate-500">No upcoming vessels on the line-up.</div>
                </div>
                <Link to="/lineup" className="btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="next-vessel-lineup-btn">
                    Open line-up <ArrowRight size={12} />
                </Link>
            </div>
        );
    }

    return (
        <>
        <div className="g-card mb-5 overflow-hidden shadow-sm" data-testid="next-vessel-card">
            <div className="flex items-stretch">
                <div className="bg-[var(--grindrod-green)] w-2" />
                <div className="flex-1 p-4 md:p-5 flex flex-wrap items-center gap-4 md:gap-6">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="bg-[var(--grindrod-navy)] text-white w-11 h-11 flex items-center justify-center rounded-sm">
                            <Boat size={22} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <div className="uppercase-label text-[10px] text-[var(--grindrod-red)]">Next Scheduled Vessel</div>
                            <div className="font-display font-black text-[var(--grindrod-navy)] text-lg md:text-xl leading-tight truncate" data-testid="next-vessel-name">
                                {next.vessel_name}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-600">
                            <User size={14} className="text-slate-400" />
                            <span className="font-semibold text-[var(--grindrod-navy)]">{next.client_name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600">
                            <Scales size={14} className="text-slate-400" />
                            <span className="tabular font-semibold text-[var(--grindrod-navy)]">{Number(next.tonnage).toLocaleString()}</span> t
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600">
                            <Calendar size={14} className="text-slate-400" />
                            <span className="font-semibold text-[var(--grindrod-navy)]">{fmtDT(next.start_datetime)}</span>
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-3 flex-wrap">
                        <div className="text-right">
                            <div className="uppercase-label text-[9px] text-slate-500">Arriving in</div>
                            <div className="font-display font-black text-[var(--grindrod-green)] text-2xl md:text-3xl tabular leading-none mt-1" data-testid="next-vessel-countdown">
                                {formatCountdown(remaining)}
                            </div>
                        </div>
                        {canStart && (
                            <button
                                onClick={() => setStartOpen(true)}
                                className="btn-primary !py-2 !px-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide"
                                data-testid="next-vessel-start-btn"
                            >
                                <Play size={14} weight="fill" /> Start vessel
                            </button>
                        )}
                        <Link to="/lineup" className="btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="next-vessel-lineup-btn">
                            Line-up <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
        {startOpen && (
            <StartVesselModal
                item={next}
                onClose={() => setStartOpen(false)}
                onStarted={onStarted}
            />
        )}
        </>
    );
}
